/**
 * Canonical entitlement keys — 3-segment `{namespace}.{entity}.{action}`
 * scheme (2026-06 re-key; see
 * profiteia-backend/docs/entitlement-rekey-implementation-plan.md).
 *
 * The backend is the authority (`/api/v2/me/access`); everything here only
 * powers the PRE-HYDRATION fallback (`activeEntitlements` from the JWT) and
 * the route-guard requirement checks. Post-cutover the JWT claim and
 * `/me/access` only ever carry canonical keys, so checks are plain set
 * lookups — no alias expansion.
 */
export const ACCESS_KEYS = Object.freeze({
  DATASET_FORWARD: 'dataset.forward.read',
  DATASET_FLEX: 'dataset.flex.read',
  DATASET_ALL: 'dataset.*.read',
  DATASET_DAY_AHEAD_PREDICTIONS: 'dataset.profiteia-day-ahead-predictions.read',
  DATASET_SPIKE_PROBABILITY_PREDICTIONS: 'dataset.profiteia-spike-probability-predictions.read',
  DATASET_CAPACITY_FEE_PREDICTIONS: 'dataset.profiteia-capacity-fee-predictions.read',
  TOOL_CONTROL_STATE: 'tool.control-state.use',
  TOOL_BIDDING_STRATEGY: 'tool.bidding-strategy.use',
  TOOL_FORWARD_BENCHMARK: 'tool.forward-benchmark.use',
  TOOL_HEDGING_BENCHMARK: 'tool.hedging-benchmark.use',
  TOOL_IMBALANCE_BENCHMARK: 'tool.imbalance-benchmark.use',
  TOOL_POWER_MARKET_SIMULATOR: 'tool.power-market-simulator.use',
  TOOL_ORDERBOOK: 'tool.orderbook.use',
  TOOL_REPORTS: 'tool.reports.use',
  TOOL_SHADOW_TRADING: 'tool.shadow-trading.use',
  TOOL_GENERATION_MAP: 'tool.generation-map.use',
  TOOL_GRID_STATE: 'tool.grid-state.use',
  TOOL_WATER_RISK: 'tool.water-risk.use',
  TOOL_DASHBOARD_BUILDER: 'tool.dashboard-builder.use',
  TOOL_GRAPH_BUILDER: 'tool.graph-builder.use',
  TOOL_ALERTS: 'tool.alerts.use',
  TOOL_CUSTOM_DASHBOARDS: 'tool.custom-dashboards.use',
  TOOL_CUSTOM_DASHBOARDS_SHARE: 'tool.custom-dashboards.share',
  CAPABILITY_NOTIFICATIONS: 'capability.notifications.receive',
  CAPABILITY_FULL_HISTORY_EXPORT: 'capability.full-history.export',
  INTEGRATION_API_USE: 'integration.api.use',
});

const ALL_TOOL_USE_KEYS = [
  ACCESS_KEYS.TOOL_CONTROL_STATE,
  ACCESS_KEYS.TOOL_BIDDING_STRATEGY,
  ACCESS_KEYS.TOOL_FORWARD_BENCHMARK,
  ACCESS_KEYS.TOOL_HEDGING_BENCHMARK,
  ACCESS_KEYS.TOOL_IMBALANCE_BENCHMARK,
  ACCESS_KEYS.TOOL_POWER_MARKET_SIMULATOR,
  ACCESS_KEYS.TOOL_ORDERBOOK,
  // Not TOOL_REPORTS: granted separately (with the Shadow Trading add-on or by
  // grant), never as part of the tools tier or the feedback plan (PI-583).
  ACCESS_KEYS.TOOL_SHADOW_TRADING,
  ACCESS_KEYS.TOOL_GENERATION_MAP,
  ACCESS_KEYS.TOOL_GRID_STATE,
  ACCESS_KEYS.TOOL_WATER_RISK,
  ACCESS_KEYS.TOOL_DASHBOARD_BUILDER,
  ACCESS_KEYS.TOOL_GRAPH_BUILDER,
  ACCESS_KEYS.TOOL_ALERTS,
  ACCESS_KEYS.TOOL_CUSTOM_DASHBOARDS,
];

const ALL_PREDICTION_DATASET_KEYS = [
  ACCESS_KEYS.DATASET_DAY_AHEAD_PREDICTIONS,
  ACCESS_KEYS.DATASET_SPIKE_PROBABILITY_PREDICTIONS,
  ACCESS_KEYS.DATASET_CAPACITY_FEE_PREDICTIONS,
];

/**
 * The keys that gate the paid tools tier: every `tool.*.use` capability plus
 * the prediction dataset feeds. Used by the tools-upgrade routing and the
 * coarse "has any tools access" checks.
 */
export const TOOL_TIER_KEYS = Object.freeze([...ALL_TOOL_USE_KEYS, ...ALL_PREDICTION_DATASET_KEYS]);

export function asEntitlementArray(entitlements) {
  if (Array.isArray(entitlements)) return entitlements.filter(Boolean);
  return entitlements ? [entitlements] : [];
}

export function hasAccessToAnyKey(userKeys, requiredKeys, deniedKeys = []) {
  const userSet = new Set(asEntitlementArray(userKeys));
  const deniedSet = new Set(asEntitlementArray(deniedKeys));

  return asEntitlementArray(requiredKeys).some((key) => !deniedSet.has(key) && userSet.has(key));
}

export function hasAccessToAllKeys(userKeys, requiredKeys, deniedKeys = []) {
  return asEntitlementArray(requiredKeys).every((required) =>
    hasAccessToAnyKey(userKeys, required, deniedKeys)
  );
}

/**
 * Dataset entities that are GROUP (packaging) keys rather than concrete
 * datasets: a plan claim like `dataset.flex.read` covers the group's member
 * datasets, but which datasets belong to a group is registry knowledge the
 * frontend does not have.
 */
const DATASET_GROUP_ENTITIES = new Set(['forward', 'flex', 'forecasts']);

/**
 * Whether one raw JWT claim satisfies one required canonical key.
 *
 * Used ONLY by the pre-hydration fallback ({@link claimsSatisfyKey}): the JWT
 * `subscription-features` claim carries UNEXPANDED Stripe feature keys —
 * plan-enterprise holds the literal `dataset.*.read` umbrella and plan-flex the
 * `dataset.flex.read` group key — while route requirements name concrete
 * keys. Post-hydration the `/me/access` allow-set is already expanded
 * server-side, so the exact-match helpers above stay authoritative there.
 *
 * Matching is deliberately PERMISSIVE for group claims (any group claim
 * matches any concrete dataset key with the same action, because membership
 * is unknown client-side): the fallback only decides while `/me/access` has
 * not landed, and the backend re-checks every request — over-showing briefly
 * beats wrongly blocking a paying user when the access fetch fails.
 */
function claimSatisfiesKey(claim, requiredKey) {
  if (claim === requiredKey) return true;

  const claimParts = String(claim).split('.');
  const requiredParts = String(requiredKey).split('.');
  if (claimParts.length !== 3 || requiredParts.length !== 3) return false;

  // Group (packaging) keys are granted explicitly per plan and are never
  // implied by an umbrella or by another group — mirrors the backend's
  // expand_wildcard semantics (registry.py: holding all-data does not imply
  // the market pages).
  const requiredIsDatasetGroup =
    requiredParts[0] === 'dataset' && DATASET_GROUP_ENTITIES.has(requiredParts[1]);

  if (claim.includes('*')) {
    if (requiredIsDatasetGroup) return false;
    return claimParts.every((part, i) => part === '*' || part === requiredParts[i]);
  }

  const [claimNs, claimEntity, claimAction] = claimParts;
  return (
    claimNs === 'dataset' &&
    DATASET_GROUP_ENTITIES.has(claimEntity) &&
    !requiredIsDatasetGroup &&
    requiredParts[0] === 'dataset' &&
    claimAction === requiredParts[2]
  );
}

/**
 * Pre-hydration twin of {@link hasAccessToAnyKey}: claim-aware matching of
 * the raw JWT claim list against required canonical keys. Explicit denies
 * still win.
 */
export function claimsSatisfyAnyKey(claims, requiredKeys, deniedKeys = []) {
  const claimList = asEntitlementArray(claims);
  const deniedSet = new Set(asEntitlementArray(deniedKeys));

  return asEntitlementArray(requiredKeys).some(
    (key) => !deniedSet.has(key) && claimList.some((claim) => claimSatisfiesKey(claim, key))
  );
}

/**
 * Pre-hydration twin of {@link hasAccessToAllKeys}.
 */
export function claimsSatisfyAllKeys(claims, requiredKeys, deniedKeys = []) {
  return asEntitlementArray(requiredKeys).every((required) =>
    claimsSatisfyAnyKey(claims, required, deniedKeys)
  );
}
